/**
 * A2AutopilotSwitch.jsx · v2.8.9.0
 * ─────────────────────────────────────
 * Interruptor maestro de Auto-agendamiento A2.
 * Se puede embeber en el Dashboard o en la vista de Reglas de Agentes.
 *
 * Props:
 *   - compact?: boolean  → variante reducida para header/dashboard
 */
import { useEffect, useState, useCallback } from "react";
import { Bot, Loader2, Info, Zap, ZapOff, Clock, Activity, ArrowUpRight, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function A2AutopilotSwitch({ compact = false }) {
  const { hasPerm } = useAuth();
  const canView = hasPerm ? hasPerm("M32.a2.view") : false;
  const canExecute = hasPerm ? hasPerm("M32.a2.execute") : false;

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/a2/autopilot/status");
      setState(data);
    } catch (e) {
      // Silencioso si no hay permiso
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar el estado del Autopilot");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    fetchStatus();
    const iv = setInterval(fetchStatus, 30000); // refrescar cada 30s
    return () => clearInterval(iv);
  }, [canView, fetchStatus]);

  const toggle = async () => {
    if (!canExecute || toggling || !state) return;
    setToggling(true);
    const nextVal = !state.enabled;
    try {
      await api.put("/a2/autopilot/status", { enabled: nextVal });
      toast.success(nextVal ? "🤖 Auto-agendamiento ACTIVADO" : "⏸  Auto-agendamiento pausado");
      setState({ ...state, enabled: nextVal });
      setTimeout(fetchStatus, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado");
    } finally { setToggling(false); }
  };

  if (!canView) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="a2-autopilot-loading">
        <Loader2 size={14} className="animate-spin" /> Cargando A2…
      </div>
    );
  }
  if (!state) return null;

  const on = state.enabled;
  const lastRun = state.last_run_at ? new Date(state.last_run_at).toLocaleString("es-EC") : "—";

  if (compact) {
    return (
      <button
        onClick={toggle}
        disabled={!canExecute || toggling}
        data-testid="a2-autopilot-toggle-compact"
        className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold transition-all ${
          on
            ? "bg-emerald-500 text-white hover:bg-emerald-600"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        } ${!canExecute ? "opacity-50 cursor-not-allowed" : ""}`}
        title={canExecute ? "Toggle Auto-agendamiento A2" : "Sin permiso para modificar"}
      >
        {toggling ? <Loader2 size={12} className="animate-spin" /> : (on ? <Zap size={12} /> : <ZapOff size={12} />)}
        A2 Autopilot · {on ? "ON" : "OFF"}
      </button>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="a2-autopilot-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${on ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
            <Bot size={22} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-[#0A3747]" style={{ fontFamily: "Space Grotesk" }}>
                Motor de Auto-agendamiento (A2)
              </h3>
              <span
                data-testid="a2-autopilot-state-badge"
                className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                  on ? "bg-emerald-500 text-white" : "bg-gray-400 text-white"
                }`}
              >
                {on ? "ACTIVO" : "PAUSADO"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {on
                ? "Las OTs aprobadas por A1 se auto-agendan al primer slot libre del técnico de zona."
                : "OTs aprobadas quedan en «Pendiente de Agendamiento» para coordinación manual."}
            </p>
          </div>
        </div>

        {/* Toggle Switch */}
        <button
          onClick={toggle}
          disabled={!canExecute || toggling}
          data-testid="a2-autopilot-toggle"
          role="switch"
          aria-checked={on}
          className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E0E621] ${
            on ? "bg-emerald-500" : "bg-gray-300"
          } ${!canExecute ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
              on ? "translate-x-8" : "translate-x-1"
            }`}
          />
          {toggling && (
            <Loader2 size={11} className="animate-spin absolute inset-0 m-auto text-white" />
          )}
        </button>
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
        <div className="bg-gray-50 rounded-lg p-2.5" data-testid="a2-metric-last-run">
          <div className="flex items-center gap-1 text-gray-500 text-[10px] uppercase font-semibold">
            <Clock size={11} /> Último ciclo
          </div>
          <div className="font-mono text-gray-800 mt-1 truncate" title={lastRun}>{lastRun}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5" data-testid="a2-metric-applied">
          <div className="flex items-center gap-1 text-gray-500 text-[10px] uppercase font-semibold">
            <Activity size={11} /> Últimas asignaciones
          </div>
          <div className="font-mono text-gray-800 mt-1">{state.last_apply_count} OTs</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5" data-testid="a2-metric-24h">
          <div className="flex items-center gap-1 text-gray-500 text-[10px] uppercase font-semibold">
            <Zap size={11} /> Últimas 24 h
          </div>
          <div className="font-mono text-gray-800 mt-1">{state.events_24h} eventos</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5" data-testid="a2-metric-interval">
          <div className="flex items-center gap-1 text-gray-500 text-[10px] uppercase font-semibold">
            <Info size={11} /> Intervalo
          </div>
          <div className="font-mono text-gray-800 mt-1">cada {Math.round((state.interval_sec || 300) / 60)} min</div>
        </div>
      </div>

      {/* v3.1.1 · Contador → ahora enlaza al carril REVISIÓN del Smart Kanban */}
      {(state.manual_pending_total ?? 0) > 0 && (
        <Link
          to="/ot-smart?view=kanban#col-manual_assignment_required"
          className="mt-3 flex items-center justify-between bg-red-50 border border-red-200 hover:bg-red-100 rounded-lg p-3 transition-colors"
          data-testid="a2-manual-rescue-alert"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500 text-white rounded-lg flex items-center justify-center">
              <ShieldAlert size={20} />
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-red-700 tracking-widest">OTs en carril REVISIÓN</div>
              <div className="text-xl font-bold text-red-800" style={{ fontFamily: "Space Grotesk" }}>
                {state.manual_pending_total}
              </div>
              <div className="text-[11px] text-red-700">Coordinador debe revisar y forzar</div>
            </div>
          </div>
          <div className="text-xs font-semibold text-red-700 flex items-center gap-1">
            Ir al Kanban <ArrowUpRight size={12} />
          </div>
        </Link>
      )}

      {/* Reglas activas · v3.8.0 · Clickeables → abren editor de reglas */}
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="a2-rules-badges">
        <span className="text-[10px] bg-blue-50 text-blue-800 px-2 py-0.5 rounded-full">🕗 L-V 08:00-18:00</span>
        <Link
          to="/admin/brain-config#a2"
          className="text-[10px] bg-purple-50 text-purple-800 px-2 py-0.5 rounded-full hover:bg-purple-100 hover:ring-2 hover:ring-purple-300 transition cursor-pointer"
          title="Editar buffer de traslado"
          data-testid="badge-buffer-edit"
        >
          🚗 Buffer {state?.rules?.buffer_min ?? 30} min · <b>Editar</b>
        </Link>
        <Link
          to="/admin/brain-config#a2"
          className="text-[10px] bg-purple-50 text-purple-800 px-2 py-0.5 rounded-full hover:bg-purple-100 hover:ring-2 hover:ring-purple-300 transition cursor-pointer"
          title="Editar horizonte de días"
          data-testid="badge-horizon-edit"
        >
          📅 Horizonte {state?.rules?.horizon_days ?? 3} días · <b>Editar</b>
        </Link>
        <span className="text-[10px] bg-amber-50 text-amber-800 px-2 py-0.5 rounded-full">⭐ VIP prioritario</span>
        <span className="text-[10px] bg-slate-50 text-slate-700 px-2 py-0.5 rounded-full">📱 Notificación WhatsApp+Email (mock)</span>
      </div>

      {state.last_error && (
        <div className="mt-3 text-[11px] text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="a2-autopilot-error">
          Último error: {state.last_error}
        </div>
      )}

      {!canExecute && (
        <div className="mt-3 text-[10px] text-gray-400 italic">
          Solo lectura · requiere permiso «M32.a2.execute» para modificar.
        </div>
      )}

      {/* Link a Bitácora */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[10px] text-gray-500">
          {state.events_24h > 0
            ? `📈 ${state.events_24h} auto-agendamiento${state.events_24h === 1 ? "" : "s"} en las últimas 24 h`
            : "Sin actividad reciente"}
        </span>
        <Link
          to="/admin/a2-bitacora"
          className="text-[11px] font-semibold text-[#0A3747] hover:text-[#135166] inline-flex items-center gap-1"
          data-testid="a2-autopilot-bitacora-link"
        >
          Ver bitácora completa <ArrowUpRight size={11} />
        </Link>
      </div>
    </div>
  );
}
